import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export function WorkspacePanel() {
  const [members, setMembers] = useState([]);
  const [release, setRelease] = useState({ version: '', hasMac: false, hasWindows: false });
  const [username, setUsername] = useState('');
  const [version, setVersion] = useState('0.2.1');
  const [hours, setHours] = useState(72);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get('/admin/orbex/workspace');
    setMembers(res.data?.members || []);
    setRelease(res.data?.release || { version: '', hasMac: false, hasWindows: false });
    if (res.data?.release?.version) setVersion(res.data.release.version);
  }, []);

  useEffect(() => {
    load().catch((err) => setError(err.response?.data?.error || 'Could not load the Orbex workspace'));
  }, [load]);

  const addMember = async (event) => {
    event.preventDefault();
    setBusy('member');
    setError('');
    setNotice('');
    try {
      await api.post('/admin/orbex/workspace/members', { username });
      setUsername('');
      await load();
      setNotice('They can sign in to Orbex with that wingr account.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add that account');
    } finally {
      setBusy('');
    }
  };

  const setBlocked = async (name, blocked) => {
    setBusy(name);
    setError('');
    setNotice('');
    try {
      await api.patch(`/admin/orbex/workspace/members/${encodeURIComponent(name)}`, { blocked });
      await load();
      setNotice(blocked
        ? `${name} is blocked. Orbex closes their library, including on a Mac or PC where the app is already installed.`
        : `${name} can sign in to Orbex again.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update that account');
    } finally {
      setBusy('');
    }
  };

  const removeMember = async (name) => {
    setBusy(name);
    setError('');
    try {
      await api.delete(`/admin/orbex/workspace/members/${encodeURIComponent(name)}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove that account');
    } finally {
      setBusy('');
    }
  };

  const copyLink = async (platform) => {
    setBusy(platform);
    setError('');
    setNotice('');
    try {
      const res = await api.post('/admin/orbex/workspace/download-link', { platform, hours: Number(hours) });
      await navigator.clipboard.writeText(res.data.url);
      setNotice(`Copied the ${platform === 'win' ? 'Windows' : 'Mac'} link for version ${res.data.version}. It stops working after ${res.data.expiresInHours} hours.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create a download link');
    } finally {
      setBusy('');
    }
  };

  const upload = async (platform, file) => {
    if (!file) return;
    setBusy(platform === 'win' ? 'upload-win' : 'upload-mac');
    setUploadProgress({ platform, percent: 0, loaded: 0, total: file.size || 0 });
    setError('');
    setNotice('');
    try {
      const body = new FormData();
      body.append('version', version);
      body.append('file', file);
      const res = await api.post(`/admin/orbex/workspace/release?platform=${platform}`, body, {
        onUploadProgress: (event) => {
          const total = event.total || file.size || 0;
          const loaded = event.loaded || 0;
          setUploadProgress({
            platform,
            loaded,
            total,
            percent: total ? Math.min(100, Math.round((loaded / total) * 100)) : 0,
          });
        },
      });
      setRelease((prev) => ({ ...prev, ...res.data }));
      setNotice(`Published Orbex ${res.data.version} for ${platform === 'win' ? 'Windows' : 'Mac'}. Download links now serve this file.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setBusy('');
      setUploadProgress(null);
    }
  };

  return (
    <section className="mt-6 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Shared workspace</h2>
      <p className="mt-1 text-sm text-gray-500">
        Add a wingr admin such as admin_5628_wr. He signs in to Orbex with that username and the same wingr password. A download link stops working after the time you choose.
      </p>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {notice && <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{notice}</p>}
      <ul className="mt-4 text-sm">
        {members.map((member) => (
          <li key={member.username} className="flex items-center gap-3 py-1">
            <span className="text-gray-900 dark:text-gray-100">{member.username}</span>
            <span className="text-gray-500">{member.role}</span>
            {member.role !== 'owner' && (
              <>
                <button type="button" className="text-red-600" disabled={busy === member.username} onClick={() => setBlocked(member.username, !member.blocked)}>
                  {member.blocked ? 'Unblock' : 'Block'}
                </button>
                <button type="button" className="text-gray-500" disabled={busy === member.username} onClick={() => removeMember(member.username)}>
                  Remove
                </button>
              </>
            )}
            {member.blocked && <span className="text-red-600">Blocked</span>}
          </li>
        ))}
      </ul>
      <form className="mt-3 flex items-center gap-2" onSubmit={addMember}>
        <input
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          placeholder="admin_5628_wr"
          className="rounded border border-gray-300 dark:border-gray-600 bg-transparent px-2 py-1 text-sm"
        />
        <button type="submit" className="rounded bg-gray-900 px-3 py-1 text-sm text-white dark:bg-gray-100 dark:text-gray-900" disabled={busy === 'member'}>
          Add admin
        </button>
      </form>
      <div className="mt-6 flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-500">
          Version
          <input
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            className="mt-1 block w-28 rounded border border-gray-300 dark:border-gray-600 bg-transparent px-2 py-1 text-gray-900 dark:text-gray-100"
          />
        </label>
        <label className="text-sm">
          <span className="text-gray-500">Mac DMG</span>
          <input className="mt-1 block text-sm" type="file" accept=".dmg" disabled={busy === 'upload-mac'} onChange={(event) => upload('mac', event.target.files?.[0])} />
        </label>
        <label className="text-sm">
          <span className="text-gray-500">Windows EXE</span>
          <input className="mt-1 block text-sm" type="file" accept=".exe" disabled={busy === 'upload-win'} onChange={(event) => upload('win', event.target.files?.[0])} />
        </label>
      </div>
      {uploadProgress && (
        <div className="mt-3 max-w-md">
          <p className="text-xs text-gray-500">
            Uploading {uploadProgress.platform === 'win' ? 'Windows' : 'Mac'}… {uploadProgress.percent}%
            {uploadProgress.total ? ` (${Math.round(uploadProgress.loaded / (1024 * 1024))} / ${Math.round(uploadProgress.total / (1024 * 1024))} MB)` : ''}
          </p>
          <div className="mt-1 h-2 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
            <div className="h-full bg-orange-500" style={{ width: `${uploadProgress.percent}%` }} />
          </div>
        </div>
      )}
      <p className="mt-2 text-xs text-gray-500">
        Published {release.version || 'nothing yet'}
        {release.hasMac ? ' · Mac ready' : ' · Mac not uploaded'}
        {release.hasWindows ? ' · Windows ready' : ' · Windows not uploaded'}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="text-sm text-gray-500">
          Link expires
          <select
            value={hours}
            onChange={(event) => setHours(Number(event.target.value))}
            className="ml-2 rounded border border-gray-300 dark:border-gray-600 bg-transparent px-2 py-1 text-gray-900 dark:text-gray-100"
          >
            <option value={1}>1 hour</option>
            <option value={6}>6 hours</option>
            <option value={24}>24 hours</option>
            <option value={72}>3 days</option>
            <option value={168}>7 days</option>
          </select>
        </label>
        <button type="button" className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1 text-sm" disabled={!release.hasMac || busy === 'mac'} onClick={() => copyLink('mac')}>
          Copy Mac link
        </button>
        <button type="button" className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1 text-sm" disabled={!release.hasWindows || busy === 'win'} onClick={() => copyLink('win')}>
          Copy Windows link
        </button>
      </div>
    </section>
  );
}

export default function OrbexAccounts() {
  const { user } = useAuth();
  const allowed = user?.role === 'superadmin' && user?.username === 'superadmin_wingr';
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limits, setLimits] = useState({});
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/orbex/desktop-accounts');
      const rows = res.data?.accounts || [];
      setAccounts(rows);
      setLimits((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          if (next[row.id] == null) next[row.id] = row.maxProfiles || 5;
        });
        return next;
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load Orbex accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
  }, [allowed, load]);

  const approve = async (id) => {
    setBusyId(id);
    setError('');
    try {
      await api.post(`/admin/orbex/desktop-accounts/${id}/approve`, {
        maxProfiles: Number(limits[id]),
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Approve failed');
    } finally {
      setBusyId('');
    }
  };

  const suspend = async (id) => {
    setBusyId(id);
    setError('');
    try {
      await api.post(`/admin/orbex/desktop-accounts/${id}/suspend`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Suspend failed');
    } finally {
      setBusyId('');
    }
  };

  if (!allowed) {
    return (
      <div className="p-8 text-sm text-gray-500">
        Only superadmin_wingr can approve Orbex accounts.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Orbex accounts</h1>
      <p className="mt-1 text-sm text-gray-500">
        Approve a desktop signup and set how many profiles that account may create.
      </p>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {loading ? (
        <p className="mt-6 text-sm text-gray-500">Loading…</p>
      ) : (
        <table className="mt-6 w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <th className="py-2 pr-4 font-medium">Email</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Profiles</th>
              <th className="py-2 pr-4 font-medium">Created</th>
              <th className="py-2 font-medium">Allowance</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((row) => (
              <tr key={row.id} className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-3 pr-4">{row.email}</td>
                <td className="py-3 pr-4">{row.status.replace('_', ' ')}</td>
                <td className="py-3 pr-4">{row.profilesUsed}{row.maxProfiles ? ` / ${row.maxProfiles}` : ''}</td>
                <td className="py-3 pr-4">{fmtDate(row.createdAt)}</td>
                <td className="py-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={limits[row.id] ?? ''}
                      onChange={(e) => setLimits((prev) => ({ ...prev, [row.id]: e.target.value }))}
                      className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-transparent px-2 py-1"
                    />
                    <button
                      type="button"
                      disabled={busyId === row.id}
                      onClick={() => approve(row.id)}
                      className="rounded bg-gray-900 px-3 py-1 text-white disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900"
                    >
                      {row.status === 'active' ? 'Update' : 'Approve'}
                    </button>
                    {row.status === 'active' && (
                      <button
                        type="button"
                        disabled={busyId === row.id}
                        onClick={() => suspend(row.id)}
                        className="rounded px-3 py-1 text-red-600 disabled:opacity-50"
                      >
                        Suspend
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!accounts.length && (
              <tr>
                <td colSpan="5" className="py-6 text-gray-500">No Orbex signups yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
